import React from 'react';
import { format, isValid, parseISO } from "date-fns";
import { formatDateTime } from "@/lib/formatDate";
import { displayAge } from "@/lib/patientAge";
import { groupPhone, Letterhead, PrintFooter, PrintStyles } from "@/components/Print/PrintParts";
import { doseMeaning, frequencyMeaning } from "@/components/Print/doseText";
import type { HeaderConfig } from '@/components/Print/UnifiedPrintModal';

interface DischargeSummaryContentProps {
  data: any;
  translations: any;
  language: string;
  clinicInfo: any;
  doctorInfo: any;
  patientInfo: any;
  settings: any;
  currentUser: any;
  headerConfig?: HeaderConfig;
  /** The treating doctor's signature image (data URL), printed above their signature line */
  signatureImage?: string | null;
}

export function DischargeSummaryContent({
                                           data,
                                           translations: t,
                                           clinicInfo,
                                           doctorInfo,
                                           patientInfo,
                                           settings,
                                           headerConfig,
                                           signatureImage,
                                         }: Readonly<DischargeSummaryContentProps>) {

  const normalizeText = (value: unknown): string =>
      value === null || value === undefined ? '' : String(value).trim();

  const hasText = (value: unknown): boolean => normalizeText(value).length > 0;

  const clinicName    = clinicInfo?.name    || '';
  const doctorName    = settings?.profile?.name   || doctorInfo?.name    || data?.admitting_doctor_name || '';
  const doctorSpec    = settings?.profile?.specialty || doctorInfo?.specialization || doctorInfo?.specialty || '';
  const doctorLicense = settings?.profile?.license   || doctorInfo?.registration_no || '';

  const isDraft = data?.status === 'admitted';
  const meds    = (data?.medicines || []).filter((m: any) => hasText(m?.drug_name));

  const patientName = normalizeText(patientInfo?.name || data?.patient_name);
  const age         = displayAge(patientInfo?.age ?? data?.patient_age, patientInfo?.date_of_birth);
  const gender      = normalizeText(patientInfo?.gender || data?.patient_gender);
  const phone       = normalizeText(patientInfo?.phone);
  const identifiers = [
    (patientInfo?.mrn || data?.patient_mrn) && `MRN ${patientInfo?.mrn || data?.patient_mrn}`,
    phone && `Ph ${groupPhone(phone)}`,
  ].filter(Boolean).join(' · ');

  const followUp = hasText(data?.follow_up_date) ? parseISO(String(data.follow_up_date).slice(0, 10)) : null;

  const muted: React.CSSProperties = { color: '#555' };
  const headingStyle: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: '#555', marginBottom: '0.5mm' };

  const Section = ({ title, value, bold }: { title: string; value?: unknown; bold?: boolean }) =>
      hasText(value) ? (
          <div style={{ marginTop: '4mm', pageBreakInside: 'avoid' }}>
            <div style={headingStyle}>{title}</div>
            <div style={{ whiteSpace: 'pre-wrap', fontWeight: bold ? 700 : undefined }}>{normalizeText(value)}</div>
          </div>
      ) : null;

  // "Morning · Night" under "1-0-1", "Twice a day" under "BD" — nothing when it would only repeat the value
  const meaningUnder = (value: unknown, meaning: string) =>
      meaning && meaning.toLowerCase() !== normalizeText(value).toLowerCase()
          ? <div className="p-muted" style={{ fontSize: '11px' }}>{meaning}</div>
          : null;

  const fact = (label: string, value: React.ReactNode) => (
      <div><span style={muted}>{label}</span> {value}</div>
  );

  return (
      <div className="p-doc">
        <PrintStyles />

        <Letterhead
            headerConfig={headerConfig}
            clinic={{ name: clinicName, address: clinicInfo?.address, phone: clinicInfo?.phone, logo: clinicInfo?.logo, letterhead: clinicInfo?.letterhead }}
            right={doctorName && <>
              <b>Dr. {doctorName}</b>
              {doctorSpec && <div className="p-small p-muted">{doctorSpec}</div>}
              {doctorLicense && <div className="p-small p-muted">Reg. No. {doctorLicense}</div>}
            </>}
        />

        {/* Title, with DRAFT beside it (until discharged) rather than a watermark over the text */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8mm', marginTop: '5mm' }}>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>
            Discharge summary
            {isDraft && (
                <span style={{ marginLeft: '3mm', padding: '0.5mm 2.5mm', border: '1.5px solid #111', fontSize: '11px', letterSpacing: '0.04em', verticalAlign: 'middle' }}>
                  DRAFT
                </span>
            )}
          </div>
          {hasText(data?.admission_number) && (
              <div style={{ textAlign: 'right', fontSize: '12px', ...muted }}>
                IPD No.
                <div style={{ color: '#111', fontSize: '14px', fontWeight: 700 }}>{data.admission_number}</div>
              </div>
          )}
        </div>

        {/* Patient and admission */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm', marginTop: '4mm', padding: '3mm 0', borderTop: '1px solid #999', borderBottom: '1px solid #999', fontSize: '12.5px' }}>
          <div>
            <b style={{ fontSize: '15px' }}>{patientName}</b>
            {(age || gender) && <span style={muted}> · {[age, gender].filter(Boolean).join(' · ')}</span>}
            {identifiers && <div style={muted}>{identifiers}</div>}
            {hasText(data?.ward_name) && fact('Ward / bed', `${data.ward_name}${data.bed_number ? ` · Bed ${data.bed_number}` : ''}`)}
          </div>
          <div>
            {fact('Admitted', formatDateTime(data?.admission_date) || '—')}
            {fact('Discharged', hasText(data?.discharge_date) ? formatDateTime(data.discharge_date) : 'Pending')}
            {doctorName && fact('Treating doctor', `Dr. ${doctorName}`)}
            {hasText(data?.consulting_doctor_name) && fact('Consultant', `Dr. ${data.consulting_doctor_name}`)}
          </div>
        </div>

        {/* Allergies: printed only when the patient has some */}
        {hasText(patientInfo?.allergies) && (
            <div style={{ marginTop: '2.5mm', padding: '1.5mm 3mm', border: '1.5px solid #111', fontWeight: 700 }}>Allergies: {normalizeText(patientInfo.allergies)}</div>
        )}

        <Section title="Chief complaints"        value={data?.complaints} />
        <Section title="Relevant history"        value={data?.patient_history} />
        <Section title="Findings on examination" value={data?.key_findings_physical} />
        <Section title="Investigation summary"   value={data?.investigation_summary} />
        <Section title="Final diagnosis"         value={data?.final_diagnosis} bold />
        <Section title="Procedure / surgery done" value={data?.procedures_done} />
        <Section title="Operative findings"       value={data?.operative_findings} />
        <Section title="Treatment given"          value={data?.treatment_given} />
        <Section title="Hospital course"          value={data?.hospital_course} />

        {hasText(data?.condition_at_discharge) && (
            <div style={{ marginTop: '4mm' }}>
              <div style={headingStyle}>Condition at discharge</div>
              <div style={{ fontWeight: 700, textTransform: 'capitalize' }}>{normalizeText(data.condition_at_discharge)}</div>
            </div>
        )}

        {/* Discharge medication */}
        {meds.length > 0 && (
            <div style={{ marginTop: '5mm' }}>
              <div style={headingStyle}>Discharge medication</div>
              <table className="p-table">
                <thead>
                  <tr>
                    <th style={{ width: '6mm' }}>#</th>
                    <th>Medicine</th>
                    <th>Dose</th>
                    <th>When</th>
                    <th>Frequency</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {meds.map((m: any, i: number) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          <div style={{ fontWeight: 700, fontSize: '14px' }}>{normalizeText(m.drug_name)}</div>
                          {hasText(m.drug_type) && <div className="p-muted p-small">{normalizeText(m.drug_type)}</div>}
                          {hasText(m.notes) && <div style={{ fontSize: '12px', marginTop: '1mm' }}>Note: {normalizeText(m.notes)}</div>}
                        </td>
                        <td>
                          {/* Short codes ("1-0-1") bold on one line; a written instruction wraps in the body font */}
                          {normalizeText(m.dose).length > 14
                            ? <div style={{ fontSize: '13px' }}>{normalizeText(m.dose)}</div>
                            : <div style={{ font: '700 15px/1.2 "Courier New", monospace', whiteSpace: 'nowrap' }}>{normalizeText(m.dose) || '—'}</div>}
                          {meaningUnder(m.dose, doseMeaning(normalizeText(m.dose), t ?? {}))}
                        </td>
                        <td>{normalizeText(m.taken_when) || '—'}</td>
                        <td>
                          {normalizeText(m.frequency) || '—'}
                          {meaningUnder(m.frequency, frequencyMeaning(normalizeText(m.frequency), t ?? {}))}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{normalizeText(m.duration) || '—'}</td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )}

        <Section title="Discharge advice"      value={data?.discharge_advice} />
        <Section title="Diet advice"           value={data?.diet_advice} />
        <Section title="Activity restriction"  value={data?.activity_restriction} />
        <Section title="Additional medication" value={data?.additional_medication} />
        <Section title="Tests requested"       value={data?.tests_requested} />
        <Section title="Other instructions"    value={data?.others} />

        {(followUp || hasText(data?.follow_up_notes)) && (
            <div style={{ alignSelf: 'flex-start', marginTop: '5mm', padding: '2mm 4mm', border: '1.5px solid #111', fontSize: '14px', pageBreakInside: 'avoid' }}>
              Follow-up{followUp && isValid(followUp) && <>: <b>{format(followUp, 'EEE, dd MMM yyyy')}</b></>}
              {hasText(data?.follow_up_notes) && <div style={{ fontSize: '13px' }}>{normalizeText(data.follow_up_notes)}</div>}
            </div>
        )}

        {hasText(data?.acknowledgement) && (
            <div style={{ marginTop: '6mm', fontStyle: 'italic' }}>
              {normalizeText(data.acknowledgement)}
            </div>
        )}

        {/* Two signatures: patient / attendant and the treating doctor */}
        <div className="p-sign" style={{ justifyContent: 'space-between' }}>
          <div>Patient / attendant signature</div>
          <div className={signatureImage ? 'p-sign-col' : undefined}>
            {signatureImage && <img className="p-sign-img" src={signatureImage} alt="" />}
            <div>
              {doctorName && <b>Dr. {doctorName}</b>}
              {doctorSpec && <div>{doctorSpec}</div>}
              {doctorLicense && <div>Reg. No. {doctorLicense}</div>}
              <div className="p-muted">Treating doctor</div>
            </div>
          </div>
        </div>

        <PrintFooter
            left={[clinicName, 'Discharge summary', data?.admission_number].filter(Boolean).join(' · ')}
            printedOn={formatDateTime(new Date())}
        />
      </div>
  );
}
